import { Box, makeStyles, Theme } from '@freighthub/silhouette-core'
import { FC } from 'react'
import { useI18n } from 'utils/localisation'

import { IPdadItem } from '../domain/PdadTypes'
import { TTransportOrCarriageMode } from '../domain/TransportMode'
import { ProgressTrackableObject } from './ProgressTrackableObject'
import { TransportModeIcon } from './TransportModeIcon'

export const tpTrackStepConnectorTestIds = {
  tpIcon: 'tp-track-step-tracker-icon',
  tpProgress: 'tp-track-step-progress',
}

interface TrackableVisualInfo {
  containerIndex: number // array index + 1
  groupNumbers?: number[]
}

export interface PdadWrapper {
  trackableVisualInfo: TrackableVisualInfo
  pdad: IPdadItem
  isNextProgress: boolean
}

export interface TpStepConnectorProps {
  pdadWrappers: PdadWrapper[]
  durationDays?: number
  transportMode?: TTransportOrCarriageMode
  showTrackableTitle: boolean
}

const useStyles = makeStyles<Theme>((theme) => ({
  stepConnector: {
    position: 'absolute',
    right: '50%',
    top: theme.spacing(2.5),
    height: 10,
    backgroundColor: theme.palette.background.default,
    zIndex: 10,
    width: 'calc(100% - 46px)',
    marginRight: theme.spacing(3),
  },
  stepConnectorInner: {
    position: 'absolute',
    left: 0,
    top: 0,
    width: 0,
    height: '100%',
    backgroundColor: theme.palette.primary.main,
  },
  connectorText: {
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    bottom: '100%',
    marginBottom: theme.spacing(2.5),
    backgroundColor: theme.palette.common.white,
    zIndex: 10,
    display: 'flex',
    alignItems: 'center',
  },
  duration: {
    position: 'absolute',
    top: theme.spacing(7.5),
    left: '50%',
    transform: 'translateX(-50%)',
    whiteSpace: 'nowrap',
  },
}))

export const getContainersGroupTitle = (numbers: number[]) => {
  return (numbers.length > 1 ? 'Containers ' : 'Container ') + numbers.sort((a, b) => a - b).join(', ')
}

export const TpTrackStepConnector: FC<TpStepConnectorProps> = ({
  durationDays,
  transportMode,
  pdadWrappers,
  showTrackableTitle,
}) => {
  const classes = useStyles()

  return (
    <div className={classes.stepConnector}>
      {pdadWrappers.map(({ pdad, trackableVisualInfo }) => {
        let width: string | number = 0
        if (pdad.passedActual) {
          width = '100%'
        } else if (pdad.isProgress) {
          width = `${pdad.progress}%`
        }
        return (
          <div
            data-testid={`${tpTrackStepConnectorTestIds.tpProgress}_${pdad.pdadType}`}
            key={trackableVisualInfo.containerIndex}
            className={classes.stepConnectorInner}
            style={{ width }}
          />
        )
      })}

      <TpTrackStepConnectorText
        durationDays={durationDays}
        transportMode={transportMode}
        transporter={pdadWrappers[0].pdad.transporter}
      />

      {showTrackableTitle &&
        pdadWrappers.map(
          ({ pdad, trackableVisualInfo }) =>
            pdad.isProgress && (
              <ProgressTrackableObject
                key={trackableVisualInfo.containerIndex}
                progress={pdad.progress}
                // @ts-expect-error TS(2345) FIXME: Argument of type 'number[] | undefined' is not ass... Remove this comment to see the full error message
                title={getContainersGroupTitle(trackableVisualInfo.groupNumbers)}
              />
            )
        )}
    </div>
  )
}

export interface StepConnectorTextProps {
  transporter?: string
  durationDays?: number
  transportMode?: TTransportOrCarriageMode
}
export const TpTrackStepConnectorText: FC<StepConnectorTextProps> = ({ durationDays, transportMode, transporter }) => {
  const { translate } = useI18n()
  const classes = useStyles()
  const hasDuration = typeof durationDays === 'number' && durationDays > 0

  return (
    <div className={classes.connectorText}>
      {transportMode && (
        <TransportModeIcon
          data-testid={`${tpTrackStepConnectorTestIds.tpIcon}-${transportMode}`}
          transportMode={transportMode}
        />
      )}
      {transporter && (
        <Box ml={1} top="-50px" whiteSpace="nowrap">
          {transporter}
        </Box>
      )}
      {hasDuration && (
        <div className={classes.duration}>{`${durationDays} ${translate('day', { count: durationDays })}`}</div>
      )}
    </div>
  )
}
